import { useEffect, useState } from 'react';
import { useLocale } from '../context';
import { useSiteContent } from '../context/SiteContentContext';
import { applySeo, seoForLocale, resolveSeoTitle } from '../utils/seo';

function pathNeedsCustomTitle(path) {
  return path.includes('/casino/game/');
}

function currentPath() {
  try {
    return window.location.pathname || '/';
  } catch {
    return '/';
  }
}

export default function SeoHead() {
  const _locale = useLocale();
  const tr = _locale.t;
  const locale = _locale.locale;
  const { seo } = useSiteContent();
  const [path, setPath] = useState(currentPath);

  useEffect(() => {
    const sync = () => setPath(currentPath());
    const onLocation = () => sync();

    // App uses history.pushState / replaceState (not only popstate).
    const origPush = history.pushState;
    const origReplace = history.replaceState;
    history.pushState = function patchedPushState(...args) {
      const ret = origPush.apply(this, args);
      window.dispatchEvent(new Event('vb:locationchange'));
      return ret;
    };
    history.replaceState = function patchedReplaceState(...args) {
      const ret = origReplace.apply(this, args);
      window.dispatchEvent(new Event('vb:locationchange'));
      return ret;
    };

    window.addEventListener('popstate', sync);
    window.addEventListener('vb:locationchange', onLocation);
    sync();

    return () => {
      history.pushState = origPush;
      history.replaceState = origReplace;
      window.removeEventListener('popstate', sync);
      window.removeEventListener('vb:locationchange', onLocation);
    };
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale || 'tr';
    if (pathNeedsCustomTitle(path)) return;

    const localized = seoForLocale(seo, locale);
    let title = localized.title;
    if (path.includes('/authProcess/registration')) title = tr('auth.register.pageTitle');
    else if (path.includes('/authProcess/login')) title = tr('auth.login.pageTitle');
    else if (path.includes('/authProcess/restorePassword')) title = tr('auth.recover.pageTitle');
    else if (path.includes('/account')) title = tr('account.pageTitle');

    applySeo({
      title: resolveSeoTitle(title, { locale }),
      description: localized.description,
      ogImage: localized.ogImage,
      favicon: localized.favicon,
      locale,
      url: window.location.href,
    });
  }, [locale, tr, seo, path]);

  // Keep title permanent if something clears it or replaces it with a URL.
  useEffect(() => {
    const guard = () => {
      if (pathNeedsCustomTitle(currentPath())) return;
      const cur = String(document.title || '').trim();
      if (!cur || /^https?:\/\//i.test(cur)) {
        applySeo({
          title: resolveSeoTitle('', { locale }),
          description: seoForLocale(seo, locale).description,
          locale,
          url: window.location.href,
        });
      }
    };
    const id = window.setInterval(guard, 4000);
    return () => window.clearInterval(id);
  }, [locale, seo]);

  return null;
}
